/**
 * A modal dialog that traps and restores focus (FRONTEND_DESIGN §8).
 *
 * Written here rather than taken from a library because §8 asks for two
 * behaviours that are usually bundled with a third this project must not have:
 *
 * - **Trap, and restore.** Tab cycles inside the dialog; closing it puts focus
 *   back on whatever opened it, so a keyboard user is returned to where they
 *   were rather than to the top of the document.
 * - **No autofocus into a control.** §8: "Do not auto-focus in a way that
 *   forces a virtual keyboard or causes a screen-reader context jump." Focus
 *   moves to the dialog *container*, which is labelled by its own heading — so
 *   a screen reader announces what this dialog is before anything else. Moving
 *   it straight into the text box would announce the box instead, which on
 *   this screen means announcing "type here" before announcing what is about
 *   to be destroyed. That is the exact jump §8 is about, and it is most
 *   tempting precisely here, where the field is the only thing to do.
 *
 * What this deliberately does *not* do, stated because the gap is real rather
 * than because it was missed: the content behind the dialog is not `inert`.
 * The keyboard cannot reach it — that is what the trap below is — but a screen
 * reader's virtual cursor can still walk it, and `aria-modal` alone does not
 * stop that in every reader. Making it inert needs the dialog to be a sibling
 * of the whole shell rather than of the panel that owns it, which is a change
 * to the shell, not to this file. §8 asks for trap and restore, and that is
 * what is here; the rest is a known limit, not a claim.
 */

import { useEffect, useRef } from 'react';

/**
 * What counts as focusable for the trap.
 *
 * Deliberately a small, closed list rather than an exhaustive one: this
 * dialog's contents are written in this feature, so a selector that tries to
 * anticipate every focusable element in the platform would be guarding against
 * inputs that cannot appear. `:not([disabled])` matters — a trap that cycled
 * onto a disabled control would strand the keyboard.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ConfirmDialogProps {
  /** Id of the element naming the dialog, which must be its heading. */
  readonly labelledBy: string;
  /** Id of the element describing what is about to happen, if there is one. */
  readonly describedBy?: string;
  readonly onDismiss: () => void;
  readonly children: React.ReactNode;
}

export function ConfirmDialog({
  labelledBy,
  describedBy,
  onDismiss,
  children,
}: ConfirmDialogProps): React.JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Captured before focus moves, restored on unmount. Read from the DOM
    // rather than passed in as a ref by the caller: whatever had focus when
    // this mounted is the honest answer, and it is not always the button the
    // caller thinks it is.
    const opener = document.activeElement;
    openerRef.current = opener instanceof HTMLElement ? opener : null;
    dialogRef.current?.focus();
    return () => {
      openerRef.current?.focus();
    };
  }, []);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onDismiss();
      return;
    }
    if (event.key !== 'Tab') return;

    const dialog = dialogRef.current;
    if (dialog === null) return;
    const focusable = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) {
      // Nothing to move to. Keeping focus where it is beats letting Tab
      // escape into a page that is `inert` — from which there would be no way
      // back without a mouse.
      event.preventDefault();
      return;
    }

    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialog)) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="dialog-scrim">
      {/*
       * `tabIndex={-1}` makes the container focusable programmatically without
       * putting it in the tab order — the same mechanism `<main>` uses for the
       * skip link, and what lets focus land on the dialog itself rather than
       * on a control inside it.
       */}
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </div>
  );
}
